import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Plus, Check, BadgeCheck, Truck, Smartphone, Package, Music2, XCircle, AlertTriangle, Star, Bell } from "lucide-react";
import { useCart } from "@/cart/CartContext";
import { ChatBubble } from "@/components/ChatBubble";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandMark = () => (
  <img src="/logo.png" alt="The Print Plug logo" className="w-12 h-12 object-contain shrink-0" />
);

/* Official TikTok single-video embed — shows ONLY this video, no related feed */
const TikTokEmbed = () => {
  useEffect(() => {
    const existing = document.querySelector('script[src="https://www.tiktok.com/embed.js"]');
    if (existing) {
      if (window.tiktok?.embed?.render) window.tiktok.embed.render();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://www.tiktok.com/embed.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);
  return (
    <blockquote
      className="tiktok-embed"
      cite="https://www.tiktok.com/@theprintplug0/video/7703262227901582606"
      data-video-id="7703262227901582606"
      style={{ maxWidth: 325, minWidth: 280, margin: 0 }}
    >
      <section>
        <a target="_blank" rel="noopener noreferrer" title="@theprintplug0" href="https://www.tiktok.com/@theprintplug0?refer=embed">
          @theprintplug0 — watch this video on TikTok
        </a>
      </section>
    </blockquote>
  );
};

const badgeClass = (badge) => {
  if (badge === "BESTSELLER") return "bg-[#39ff14] text-black";
  if (badge === "CINEMATIC") return "bg-[#f5b700] text-black";
  return "bg-white text-black";
};

const ProductCard = ({ product, animClass }) => {
  const { addItem } = useCart();
  const variants = product.variants || [];
  const [selected, setSelected] = useState(variants[0] || null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notified, setNotified] = useState(false);
  const price = selected ? selected.price_cents : product.price_cents;
  const soldOut = Boolean(product.sold_out);
  // Per-variant artwork: a variant can carry its own image (e.g. $20 vs $50 template)
  const displayImage = (selected && selected.image) || product.image;

  const notify = async () => {
    try {
      await axios.post(`${API}/restock-notify`, { product_id: product.product_id, email: notifyEmail.trim() });
      setNotified(true);
      toast.success("You're on the list — we'll email you when it's back");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enter a valid email");
    }
  };

  return (
    <article
      data-testid={`product-card-${product.product_id}`}
      className={`pp-card overflow-hidden group grid grid-cols-1 lg:grid-cols-2 ${animClass}`}
    >
      <figure className="relative aspect-square lg:aspect-auto lg:min-h-[420px] bg-[#1a1c22] flex items-end overflow-hidden">
        <img
          src={displayImage}
          alt={product.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-500"
        />
        <figcaption className="relative z-10 text-xs uppercase tracking-[0.14em] text-white f-mono p-5 pt-10 w-full bg-gradient-to-t from-black/80 to-transparent">
          {product.name}
        </figcaption>
        {product.badge && (
          <span className={`absolute top-4 right-4 z-10 ${badgeClass(product.badge)} text-[10px] f-mono font-bold px-2 py-0.5 uppercase rounded`}>
            {product.badge}
          </span>
        )}
        <span className="absolute top-4 left-4 z-10 bg-black/70 backdrop-blur border border-[#f5b700]/40 text-[#f5b700] text-[9px] f-mono font-bold px-2 py-1 uppercase tracking-wider rounded" data-testid={`prop-disclosure-${product.product_id}`}>
          Prop / Collector Replica
        </span>
      </figure>
      <div className="p-6 sm:p-8 flex flex-col justify-between gap-6">
        <div>
          <div className="flex justify-between items-baseline mb-2 gap-3">
            <h3 className="f-disp text-2xl sm:text-3xl uppercase text-white tracking-tight">{product.name}</h3>
            <span data-testid={`product-price-${product.product_id}`} className="f-mono text-2xl font-bold text-[#39ff14] tabular-nums whitespace-nowrap">
              ${(price / 100).toFixed(2)}
            </span>
          </div>
          {product.description && (
            <p className="text-sm text-[#9ca3af] f-body leading-relaxed">{product.description}</p>
          )}
          {product.features?.length > 0 && (
            <ul className="mt-4 space-y-1.5 f-mono text-[11px] text-[#ecebe4]">
              {product.features.map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check size={12} className="text-[#39ff14]" strokeWidth={3} /> {f}
                </li>
              ))}
            </ul>
          )}
          {variants.length > 0 && (
            <div className="mt-6">
              <p className="f-mono text-[10px] text-[#9ca3af] uppercase tracking-[0.2em] mb-3">Choose Your Option</p>
              <div className="grid grid-cols-2 gap-3" data-testid="variant-selector">
                {variants.map((v) => (
                  <button
                    key={v.variant_id}
                    type="button"
                    data-testid={`variant-${v.variant_id}`}
                    onClick={() => setSelected(v)}
                    className={`p-4 border rounded-lg text-left transition-all duration-300 ${
                      selected?.variant_id === v.variant_id
                        ? "border-[#39ff14] bg-[#39ff14]/10 d1-glow"
                        : "border-[#22242a] bg-[#0a0a0c] hover:border-[#39ff14]/50"
                    }`}
                  >
                    <span className="f-head text-xl font-bold uppercase text-white block">{v.label}</span>
                    {v.face && (
                      <span className="f-mono text-[10px] text-[#9ca3af] uppercase tracking-wider block">{v.face}</span>
                    )}
                    <span className={`f-mono text-sm font-bold tabular-nums block mt-1 ${selected?.variant_id === v.variant_id ? "text-[#39ff14]" : "text-white"}`}>
                      ${(v.price_cents / 100).toFixed(2)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {soldOut ? (
          <div className="space-y-3" data-testid={`sold-out-${product.product_id}`}>
            <div className="w-full py-4 bg-[#22242a] text-[#9ca3af] f-mono text-xs uppercase tracking-[0.14em] font-bold text-center rounded-lg">
              Sold Out
            </div>
            {notified ? (
              <p className="f-mono text-[11px] text-[#39ff14] uppercase tracking-wider text-center flex items-center justify-center gap-2">
                <Bell size={12} /> You're on the restock list
              </p>
            ) : (
              <div className="flex gap-2">
                <input
                  data-testid={`notify-email-${product.product_id}`}
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder="Email for restock alert"
                  className="flex-1 bg-[#0a0a0c] border border-[#22242a] rounded-lg px-3 py-2.5 text-xs f-body text-white placeholder-[#9ca3af] focus:outline-none focus:border-[#39ff14]"
                />
                <button
                  type="button"
                  data-testid={`notify-button-${product.product_id}`}
                  onClick={notify}
                  className="px-4 bg-[#39ff14] text-black f-mono text-[10px] uppercase tracking-[0.14em] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-1.5"
                >
                  <Bell size={12} strokeWidth={2.5} /> Notify Me
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            data-testid={`add-to-cart-${product.product_id}`}
            onClick={() => addItem(product, selected)}
            className="w-full py-4 bg-[#39ff14] hover:bg-white text-black f-mono text-xs uppercase tracking-[0.14em] font-bold rounded-lg transition-all flex items-center justify-center gap-2 d1-glow pp-btn-glow"
          >
            <Plus size={14} strokeWidth={3} />
            Add to Cart{selected ? ` — ${selected.label}` : ""}
          </button>
        )}
      </div>
    </article>
  );
};

export default function StorePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [announcement, setAnnouncement] = useState(null);
  const [category, setCategory] = useState("All");
  const [reviews, setReviews] = useState([]);
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const { count, items, totalCents, removeItem } = useCart();

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
    axios
      .get(`${API}/announcement`)
      .then((r) => r.data.active && setAnnouncement(r.data.text))
      .catch(() => {});
    axios
      .get(`${API}/reviews`)
      .then((r) => setReviews(r.data.reviews || []))
      .catch(() => {});
  }, []);

  const categories = ["All", ...new Set(products.map((p) => p.category).filter(Boolean))];
  const visible = category === "All" ? products : products.filter((p) => p.category === category);

  const cardAnim = ["d1-card-1", "d1-card-2", "d1-card-3"];

  return (
    <div className="bg-[#0a0a0c] text-[#ecebe4] min-h-screen selection:bg-[#39ff14] selection:text-black">
      {announcement && (
        <div data-testid="announcement-bar" className="bg-[#39ff14] text-black text-center f-mono text-xs font-bold uppercase tracking-[0.14em] py-2.5 px-4">
          {announcement}
        </div>
      )}
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#0a0a0c]/90 backdrop-blur-xl border-b border-[#22242a] px-4 sm:px-8 lg:px-12 py-4 flex items-center justify-between shadow-2xl">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div className="flex flex-col">
            <span data-testid="brand-name" className="f-disp tracking-wider text-xl uppercase leading-none text-white">
              THE PRINT PLUG<span className="text-[#39ff14]">.</span>
            </span>
            <span className="f-mono text-[10px] text-[#ef1a1a] tracking-widest uppercase">
              RichOffMotion Prop
            </span>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-8 text-xs f-mono uppercase tracking-[0.14em] text-[#9ca3af]">
          <a href="#catalog" data-testid="nav-catalog-link" className="hover:text-[#39ff14] transition-colors">Catalog</a>
          <a href="#shipping" data-testid="nav-shipping-link" className="hover:text-[#39ff14] transition-colors">Shipping</a>
          <a href="#reviews" data-testid="nav-reviews-link" className="hover:text-[#39ff14] transition-colors">Reviews</a>
          <Link to="/track" data-testid="nav-track-link" className="hover:text-[#39ff14] transition-colors">Track Order</Link>
          <a href="https://www.tiktok.com/@theprintplug0" target="_blank" rel="noopener noreferrer" data-testid="nav-tiktok-link" className="hover:text-[#39ff14] transition-colors">TikTok</a>
          <a href="#policy" data-testid="nav-policy-link" className="hover:text-[#39ff14] transition-colors">Refund Policy</a>
        </div>
        {/* Cart button + mini-cart preview */}
        <div className="relative">
          <button
            type="button"
            data-testid="nav-cart-button"
            onClick={() => setMiniCartOpen((o) => !o)}
            className="relative f-mono text-xs uppercase tracking-[0.14em] px-5 py-2.5 bg-[#39ff14] text-black font-bold rounded-lg hover:bg-white transition-all d1-glow pp-btn-glow flex items-center gap-2"
          >
            <ShoppingCart size={14} strokeWidth={2.5} />
            Cart
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-[#f5b700] text-black text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {count}
              </span>
            )}
          </button>
          {miniCartOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMiniCartOpen(false)} />
              <div data-testid="mini-cart" className="absolute right-0 top-full mt-2 z-50 w-80 max-w-[calc(100vw-2rem)] bg-[#121316] border border-[#22242a] shadow-2xl">
                <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest px-4 pt-4 pb-2 border-b border-[#22242a]">
                  Your Stack ({count})
                </p>
                {items.length === 0 ? (
                  <p className="f-body text-xs text-[#8e929b] px-4 py-6 text-center">Cart is empty.</p>
                ) : (
                  <>
                    <div className="max-h-64 overflow-y-auto">
                      {items.map((i) => (
                        <div key={i.cart_key} data-testid={`mini-cart-item-${i.cart_key}`} className="flex items-center gap-3 px-4 py-3 border-b border-[#22242a]">
                          <img src={i.image} alt={i.name} className="w-10 h-10 object-cover shrink-0 bg-[#1a1c22]" />
                          <div className="flex-1 min-w-0">
                            <p className="f-disp text-xs uppercase text-white truncate">{i.name}</p>
                            <p className="f-mono text-[10px] text-[#8e929b]">×{i.qty}</p>
                          </div>
                          <span className="f-mono text-xs text-[#39ff14] tabular-nums shrink-0">${((i.price_cents * i.qty) / 100).toFixed(2)}</span>
                          <button
                            data-testid={`mini-cart-remove-${i.cart_key}`}
                            onClick={() => removeItem(i.cart_key)}
                            className="text-[#8e929b] hover:text-red-500 transition-colors shrink-0"
                            aria-label="Remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="px-4 py-3 border-t border-[#22242a]">
                      <div className="flex justify-between mb-3">
                        <span className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest">Subtotal</span>
                        <span data-testid="mini-cart-subtotal" className="f-disp text-lg text-white tabular-nums">${(totalCents / 100).toFixed(2)}</span>
                      </div>
                      <Link
                        to="/cart"
                        data-testid="mini-cart-view"
                        onClick={() => setMiniCartOpen(false)}
                        className="block text-center f-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2.5 bg-[#39ff14] text-black font-bold hover:bg-white transition-colors"
                      >
                        Review & Checkout
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <header className="relative overflow-hidden bg-[#0a0a0c] text-white min-h-[85vh] flex items-center border-b border-[#22242a]">
        <img
          src="https://images.unsplash.com/photo-1680251590844-53784815ded4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBkYXJRJTIwc3RyZWV0JTIwc3R5bGUlMjBwcmludGluZyUyMG1vbmV5JTIwdXJiYW4lMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzkzNzYwODMwfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c]/70 via-[#0a0a0c]/80 to-[#0a0a0c]"></div>
        <div className="absolute -right-20 -top-20 w-[500px] h-[500px] bg-[#39ff14]/10 rounded-full blur-3xl pointer-events-none d1-pulse"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-20 md:py-28 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 min-w-0">
              <p className="f-mono text-xs uppercase tracking-[0.2em] text-[#39ff14] mb-6 d1-fade" data-testid="hero-disclosure-badge">
                Prop &amp; Replica Precision Printouts · Disclosed for Film &amp; Collector Use
              </p>
              <h1 className="f-disp text-[clamp(32px,4.5vw,68px)] font-black leading-[0.98] tracking-tight uppercase text-white mb-8">
                <span className="ln"><span>Most realistic</span></span>
                <span className="ln"><span className="text-[#39ff14]">bills on</span></span>
                <span className="ln"><span>the market.</span></span>
              </h1>
              <div data-testid="hero-intro-line" className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-6 d1-fade f-mono text-xs uppercase tracking-[0.14em] text-[#9ca3af]">
                <span className="flex items-center gap-2">
                  <BadgeCheck size={14} className="text-[#39ff14]" strokeWidth={2.5} /> Legit seller
                </span>
                <span className="flex items-center gap-2">
                  <Truck size={14} className="text-[#39ff14]" strokeWidth={2.5} /> Ships next business day
                </span>
                <span className="flex items-center gap-2">
                  <Smartphone size={14} className="text-[#39ff14]" strokeWidth={2.5} /> Tracking emailed automatically
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4 d1-fade mb-8">
                <a
                  href="#catalog"
                  data-testid="hero-shop-cta"
                  className="f-mono text-sm uppercase tracking-[0.14em] px-10 py-5 bg-[#39ff14] text-black font-bold rounded-lg hover:bg-white transition-colors d1-glow pp-btn-glow shadow-[0_0_30px_rgba(57,255,20,0.35)]"
                >
                  Explore Bundles
                </a>
                <a
                  href="#reviews"
                  className="f-mono text-xs uppercase tracking-[0.14em] px-8 py-4 bg-[#121316]/80 backdrop-blur border border-[#22242a] text-white font-bold rounded-lg hover:border-[#39ff14]/50 transition-colors"
                >
                  Read Reviews
                </a>
              </div>
              <div data-testid="hero-trust-info" className="flex flex-wrap items-center gap-x-6 gap-y-2 d1-fade border-t border-[#22242a] pt-5">
                <span className="flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.14em] text-[#ecebe4]">
                  <Star size={13} className="text-[#39ff14] fill-[#39ff14]" strokeWidth={2} /> 5-star rated by verified buyers
                </span>
                <span className="flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.14em] text-[#ecebe4]">
                  <BadgeCheck size={13} className="text-[#39ff14]" strokeWidth={2.5} /> Secure checkout — Stripe
                </span>
                <span className="flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.14em] text-[#ecebe4]">
                  <Truck size={13} className="text-[#39ff14]" strokeWidth={2.5} /> Free shipping over $100
                </span>
              </div>
            </div>
            <div className="lg:col-span-5 min-w-0 flex items-center justify-center py-6 lg:py-10">
              <img
                src="/logo.png"
                alt="The Print Plug — graffiti logo with bill counter and prop stacks"
                data-testid="hero-logo"
                className="w-full max-w-sm drop-shadow-[0_0_45px_rgba(57,255,20,0.35)] d1-fade"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Trust strip */}
      <section className="bg-[#121316] border-b border-[#22242a] py-6" data-testid="trust-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: BadgeCheck, title: "Secure Checkout", note: "256-bit SSL · Stripe" },
            { icon: Truck, title: "Next-Day Dispatch", note: "USPS drop-off" },
            { icon: Package, title: "Tracking Included", note: "Emailed automatically" },
            { icon: Star, title: "Real Reviews", note: "Verified buyers only" },
          ].map((t) => (
            <div key={t.title} className="flex items-center gap-3">
              <t.icon size={18} className="text-[#39ff14] shrink-0" strokeWidth={2} />
              <div>
                <p className="f-head text-sm font-bold uppercase text-white tracking-wide">{t.title}</p>
                <p className="f-mono text-[9px] text-[#9ca3af] uppercase tracking-wider">{t.note}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Catalog */}
      <section id="catalog" className="py-16 md:py-24 bg-[#0a0a0c] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="f-disp text-[clamp(32px,4vw,56px)] leading-[0.95] tracking-[-0.03em] uppercase">
              Catalog
            </h2>
            {categories.length > 2 && (
              <div className="flex gap-2 mt-6" data-testid="category-tabs">
                {categories.map((c) => (
                  <button
                    key={c}
                    data-testid={`category-tab-${c.toLowerCase()}`}
                    onClick={() => setCategory(c)}
                    className={`f-mono text-xs uppercase tracking-[0.14em] px-5 py-2.5 font-bold transition-colors ${
                      category === c
                        ? "bg-[#39ff14] text-black"
                        : "bg-[#121316] border border-[#22242a] text-[#8e929b] hover:text-[#39ff14]"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {loading && (
            <p data-testid="catalog-loading" className="f-mono text-xs text-[#8e929b] uppercase tracking-[0.14em]">
              Loading stacks…
            </p>
          )}

          <div className="max-w-5xl mx-auto space-y-8" data-testid="product-grid">
            {visible.map((p, idx) => (
              <ProductCard key={p.product_id} product={p} animClass={cardAnim[idx % 3]} />
            ))}
          </div>
        </div>
      </section>

      {/* How Shipping Works — owner's own words */}
      <section id="shipping" className="py-12 md:py-16 bg-[#121316] text-white border-y border-[#22242a]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <h2 className="f-disp text-[clamp(22px,2.6vw,32px)] font-bold leading-tight tracking-tight uppercase mb-10 text-center">
            How Shipping Works
          </h2>
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-testid="shipping-steps">
            {/* progress line connecting the steps */}
            <div className="hidden md:block absolute top-[30px] left-[16%] right-[16%] h-[2px] bg-gradient-to-r from-[#39ff14] via-[#39ff14]/50 to-[#39ff14] pointer-events-none" />
            <div className="md:hidden absolute left-[29px] top-[10%] bottom-[10%] w-[2px] bg-gradient-to-b from-[#39ff14] via-[#39ff14]/50 to-[#39ff14] pointer-events-none" />
            {[
              { icon: ShoppingCart, num: "01", title: "Order Confirmed", text: "Complete your purchase securely through our website. You'll get an order confirmation email right away." },
              { icon: Package, num: "02", title: "Processing & Tracking", text: "Your order is carefully prepared. Tracking information is emailed to you once your shipping label is created." },
              { icon: Truck, num: "03", title: "USPS Handoff", text: "Orders are dropped off with USPS the next business day after processing. Follow your package with the tracking number provided." },
            ].map((s, i) => (
              <div key={s.num} className={`relative bg-[#0a0a0c] border border-[#39ff14]/25 rounded-xl p-6 md:p-6 text-left md:text-center hover:border-[#39ff14]/60 hover:shadow-[0_0_24px_rgba(57,255,20,0.10)] transition-all duration-300 flex md:block gap-5 ${cardAnim[i]}`}>
                <div className="relative z-10 w-[60px] h-[60px] shrink-0 md:mx-auto md:mb-4 rounded-full bg-[#0a0a0c] border-2 border-[#39ff14] shadow-[0_0_20px_rgba(57,255,20,0.35)] flex flex-col items-center justify-center">
                  <s.icon size={18} className="text-[#39ff14]" strokeWidth={2.5} />
                  <span className="f-mono text-[8px] font-bold text-[#39ff14] mt-0.5">{s.num}</span>
                </div>
                <div>
                  <p className="f-head text-lg font-bold uppercase text-white tracking-wide mb-1.5 mt-1 md:mt-0">{s.title}</p>
                  <p className="f-body text-xs text-[#9ca3af] leading-relaxed">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest TikTok */}
      <section id="tiktok" className="py-12 md:py-16 bg-[#0a0a0c] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="flex-1 min-w-0 text-center md:text-left">
              <p className="f-mono text-[10px] text-[#39ff14] uppercase tracking-[0.25em] mb-2">Latest Drop</p>
              <h2 className="f-disp text-[clamp(24px,3vw,36px)] font-bold leading-tight tracking-tight uppercase mb-4">
                See What's New
              </h2>
              <p className="f-body text-sm text-[#9ca3af] leading-relaxed mb-8 max-w-md mx-auto md:mx-0">
                Stay connected with The Print Plug. Follow our TikTok for product updates, new releases, and behind-the-scenes content.
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                <a
                  href="https://www.tiktok.com/@theprintplug0"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tiktok-follow-button"
                  className="inline-flex items-center gap-2 f-mono text-xs uppercase tracking-[0.14em] px-7 py-3.5 bg-[#39ff14] text-black font-bold rounded-lg hover:bg-white transition-all d1-glow pp-btn-glow"
                >
                  <Music2 size={14} strokeWidth={2.5} />
                  Follow @theprintplug0
                </a>
                <a
                  href="https://www.tiktok.com/@theprintplug0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="f-mono text-xs uppercase tracking-[0.14em] text-[#9ca3af] hover:text-[#39ff14] transition-colors"
                >
                  Watch Our Latest Videos →
                </a>
              </div>
            </div>
            <div className="shrink-0 rounded-2xl border border-[#39ff14]/40 shadow-[0_0_30px_rgba(57,255,20,0.15)] bg-[#121316] p-3">
              <div data-testid="tiktok-latest-video" className="rounded-xl overflow-hidden bg-[#0a0a0c] min-h-[500px] flex items-start justify-center">
                <TikTokEmbed />
              </div>
              <div className="flex items-center justify-between gap-3 px-2 pt-3">
                <span className="f-mono text-[9px] uppercase tracking-wider text-[#8e929b]">@theprintplug0 only — no related videos</span>
                <a
                  href="https://www.tiktok.com/t/ZTP1dPm81/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tiktok-video-link"
                  className="f-mono text-[9px] uppercase tracking-wider font-bold text-[#39ff14] hover:text-white transition-colors"
                >
                  Watch on TikTok →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Refund Policy — owner's exact words */}
      <section id="policy" className="py-12 md:py-16 bg-[#121316] text-white border-y border-[#22242a]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-[#0a0a0c] border border-red-500/40 p-8" data-testid="refund-policy" id="refund-policy">
            <h2 className="f-disp text-[clamp(22px,2.5vw,32px)] leading-[0.95] tracking-[-0.03em] uppercase mb-6 text-red-400">
              No Refund Policy — Please Read!
            </h2>
            <ul className="space-y-4 f-body text-sm text-[#ecebe4]">
              <li className="flex items-start gap-3">
                <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" strokeWidth={2.5} />
                No refunds on templates.
              </li>
              <li className="flex items-start gap-3">
                <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" strokeWidth={2.5} />
                No refunds on orders once placed.
              </li>
              <li className="flex items-start gap-3">
                <Check size={16} className="text-[#39ff14] mt-0.5 shrink-0" strokeWidth={2.5} />
                The ONLY exception: a refund will be issued if there is a delay caused on our end.
              </li>
            </ul>
            <p className="f-body text-xs text-[#8e929b] leading-relaxed mt-6 flex items-start gap-3">
              <AlertTriangle size={14} className="text-[#f5b700] mt-0.5 shrink-0" strokeWidth={2.5} />
              Please make sure you understand what you&apos;re purchasing before placing your order.
              By placing an order, you agree to our refund policy.
            </p>
            <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-[0.2em] mt-4">
              Thank you for your understanding and support! — The Print Plug
            </p>
          </div>
        </div>
      </section>

      <ChatBubble />

      {/* Customer Reviews — real buyers, owner-approved */}
      {reviews.length > 0 && (
        <section id="reviews" className="py-12 md:py-16 bg-[#0a0a0c] text-white">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="f-disp text-[clamp(24px,3vw,36px)] leading-[0.95] tracking-[-0.03em] uppercase mb-8">
              What Buyers Say
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="reviews-grid">
              {reviews.map((r, i) => (
                <div key={i} className="bg-[#121316] border border-[#22242a] p-6">
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={14} className={n <= r.rating ? "text-[#39ff14] fill-[#39ff14]" : "text-[#22242a]"} strokeWidth={1.5} />
                    ))}
                  </div>
                  <p className="f-body text-sm text-[#ecebe4] leading-relaxed mb-3">&ldquo;{r.text}&rdquo;</p>
                  <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest">— {r.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-[#0a0a0c] text-[#8e929b] border-t border-[#22242a] pt-14 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12" data-testid="footer-columns">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo.png" alt="The Print Plug logo" className="w-9 h-9 object-contain" />
                <span className="f-disp text-lg text-white">THE PRINT PLUG</span>
              </div>
              <p className="f-mono text-[10px] text-[#ef1a1a] tracking-widest uppercase mb-3">RichOffMotion Prop</p>
              <p className="f-body text-xs leading-relaxed">Most realistic bills on the market. Ships worldwide via USPS.</p>
            </div>
            <div>
              <p className="f-mono text-[10px] text-white uppercase tracking-[0.2em] mb-4">Shop</p>
              <ul className="space-y-2.5 text-xs f-body">
                <li><a href="#catalog" className="hover:text-[#39ff14] transition-colors">Catalog</a></li>
                <li><Link to="/track" className="hover:text-[#39ff14] transition-colors">Track My Order</Link></li>
                <li><Link to="/cart" className="hover:text-[#39ff14] transition-colors">Cart</Link></li>
              </ul>
            </div>
            <div>
              <p className="f-mono text-[10px] text-white uppercase tracking-[0.2em] mb-4">Support</p>
              <ul className="space-y-2.5 text-xs f-body">
                <li><a href="#policy" className="hover:text-[#39ff14] transition-colors">Refund Policy</a></li>
                <li><a href="#shipping" className="hover:text-[#39ff14] transition-colors">How Shipping Works</a></li>
                <li><button type="button" onClick={() => document.querySelector('[data-testid="chat-bubble-button"]')?.click()} className="hover:text-[#39ff14] transition-colors">Live Chat</button></li>
                <li><a href="mailto:sales@theprintplug.net" className="hover:text-[#39ff14] transition-colors">Email Us</a></li>
              </ul>
            </div>
            <div>
              <p className="f-mono text-[10px] text-white uppercase tracking-[0.2em] mb-4">Follow</p>
              <ul className="space-y-2.5 text-xs f-body">
                <li>
                  <a href="https://www.tiktok.com/@theprintplug0" target="_blank" rel="noopener noreferrer" className="hover:text-[#39ff14] transition-colors flex items-center gap-2">
                    <Music2 size={12} /> @theprintplug0
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#22242a] pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] f-mono uppercase tracking-wider">
            <span>© {new Date().getFullYear()} The Print Plug. All rights reserved.</span>
            <span>For motion picture &amp; media production use only · Not legal tender</span>
            <span>Secure checkout · USPS worldwide</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
